import React, { ReactElement, useEffect, useRef, useState } from 'react'
import { Flex, Stack } from '@mindfulchefuk/design-system'

import { DROPDOWN_CLOSING_DELAY } from '@mindfulchefuk/constants'
import { TMealPlan } from '@mindfulchefuk/config/mealPlanConfig'
import { PortionCount } from '@mindfulchefuk/config/portionCountConfig'
import { RecipeFilterType } from '@mindfulchefuk/features/Recipes/interfaces'
import { useRouter } from 'next/router'
import { trackAnalyticsEvent } from '@mindfulchefuk/utils/analytics/amplitude/amplitudeAdapter'
import { usePageSource } from '@mindfulchefuk/utils/analytics/amplitude/hooks/usePageSource'
import { RecipeSortId } from '@mindfulchefuk/config/sortOptionsConfig'
import { OnboardingChooseRecipesMealPlanFilter } from '@mindfulchefuk/onboarding/components/OnboardingChooseRecipes/OnboardingChooseRecipesFilters/OnboardingChooseRecipesMealPlanFilter/OnboardingChooseRecipesMealPlanFilter'
import { OnboardingChooseRecipesPortionCountFilter } from '@mindfulchefuk/onboarding/components/OnboardingChooseRecipes/OnboardingChooseRecipesFilters/OnboardingChooseRecipesPortionCountFilter'
import { OnboardingChooseRecipesSortFilter } from '@mindfulchefuk/onboarding/components/OnboardingChooseRecipes/OnboardingChooseRecipesFilters/OnboardingChooseRecipesSortFilter/OnboardingChooseRecipesSortFilter'
import { DeprecatedRecipeFilterCalendar } from '@mindfulchefuk/components/Recipes/LoggedInChooseRecipesFilters/DeprecatedRecipeFilterCalendar'
import { TOnboardingRecipeFilterHandlers } from '@mindfulchefuk/onboarding/components/OnboardingChooseRecipes/OnboardingChooseRecipesFilters/OnboardingRecipeFilterHandlers.types'

export type TOnboardingRecipeFilters = {
  mealPlan: TMealPlan
  portionCount: PortionCount
  date: string
  sortBy: RecipeSortId
  onDateChange: TOnboardingRecipeFilterHandlers['date']
  onPortionCountChange: TOnboardingRecipeFilterHandlers['portionCount']
  onMealPlanChange: TOnboardingRecipeFilterHandlers['mealPlan']
  onSortByChange: TOnboardingRecipeFilterHandlers['sortBy']
}

export const DeprecatedOnboardingRecipeFilters = ({
  mealPlan,
  portionCount,
  date,
  sortBy,
  onDateChange,
  onPortionCountChange,
  onMealPlanChange,
  onSortByChange,
}: TOnboardingRecipeFilters): ReactElement => {
  const router = useRouter()
  const pageSource = usePageSource()

  const [openRecipeFilter, setOpenRecipeFilter] =
    useState<RecipeFilterType>('portion-count')

  const handleToggle = (value: RecipeFilterType) => {
    if (openRecipeFilter !== value) {
      trackAnalyticsEvent({
        name: 'expand filter / sort',
        data: {
          'filter type': value as 'portion-count' | 'food-groups' | 'sort-by',
          'page source': pageSource,
        },
      })
    }

    setOpenRecipeFilter((prevOpenRecipeFilter: RecipeFilterType) =>
      prevOpenRecipeFilter === value ? null : value
    )
  }

  const ref = useRef(null)

  useEffect(() => {
    if (typeof IntersectionObserver !== 'function') return undefined

    // eslint-disable-next-line compat/compat
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.boundingClientRect.bottom < 0) {
        setOpenRecipeFilter(null)
      }
    })

    if (ref.current) {
      observer.observe(ref.current)
    }
    return () => observer.disconnect()
  })

  useEffect(() => {
    if (router.isReady) {
      const { initialPortionCount } = router.query
      setOpenRecipeFilter(initialPortionCount ? 'calendar' : 'portion-count')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady])

  const handleDateChange: TOnboardingRecipeFilterHandlers['date'] = () => {
    trackAnalyticsEvent({
      name: 'filter / sort by',
      data: {
        'recipe portion count': portionCount,
        'sort selected': sortBy,
        'meal plan selected': mealPlan,
        'page source': pageSource,
      },
    })
    onDateChange()
    setTimeout(setOpenRecipeFilter, DROPDOWN_CLOSING_DELAY, null)
  }

  const handlePortionCountChange: TOnboardingRecipeFilterHandlers['portionCount'] =
    (newPortionCount) => {
      trackAnalyticsEvent({
        name: 'filter / sort by',
        data: {
          'recipe portion count': newPortionCount,
          'sort selected': sortBy,
          'meal plan selected': mealPlan,
          'page source': pageSource,
        },
      })
      onPortionCountChange(newPortionCount)
      setTimeout(setOpenRecipeFilter, DROPDOWN_CLOSING_DELAY, null)
    }

  const handleMealPlanChange: TOnboardingRecipeFilterHandlers['mealPlan'] = (
    newMealPlan
  ) => {
    trackAnalyticsEvent({
      name: 'filter / sort by',
      data: {
        'recipe portion count': portionCount,
        'sort selected': sortBy,
        'meal plan selected': newMealPlan,
        'page source': pageSource,
      },
    })
    onMealPlanChange(newMealPlan)
    setTimeout(setOpenRecipeFilter, DROPDOWN_CLOSING_DELAY, null)
  }

  const handleSortByChange: TOnboardingRecipeFilterHandlers['sortBy'] = (
    newSortBy
  ) => {
    trackAnalyticsEvent({
      name: 'filter / sort by',
      data: {
        'recipe portion count': portionCount,
        'sort selected': newSortBy,
        'meal plan selected': mealPlan,
        'page source': pageSource,
      },
    })
    onSortByChange(newSortBy)
    setTimeout(setOpenRecipeFilter, DROPDOWN_CLOSING_DELAY, null)
  }

  return (
    <Flex
      display={{ base: 'block', md: 'flex' }}
      justify="space-between"
      mt={8}
      mb={-4}
      ref={ref}
    >
      <Stack direction={['column', 'row', 'row']} spacing={[12, 40, 32]}>
        <OnboardingChooseRecipesMealPlanFilter
          isOpen={openRecipeFilter === 'meal-plan'}
          mealPlan={mealPlan}
          onChange={handleMealPlanChange}
          onToggle={() => handleToggle('meal-plan')}
        />

        <DeprecatedRecipeFilterCalendar
          date={date}
          isOpen={openRecipeFilter === 'calendar'}
          onChange={handleDateChange}
          onToggle={() => handleToggle('calendar')}
        />

        <OnboardingChooseRecipesPortionCountFilter
          isOpen={openRecipeFilter === 'portion-count'}
          onChange={handlePortionCountChange}
          onToggle={() => handleToggle('portion-count')}
          portionCount={portionCount}
        />
      </Stack>
      <Flex justifyContent={{ md: 'flex-end' }} mt={{ base: 12, md: 0 }}>
        <OnboardingChooseRecipesSortFilter
          isOpen={openRecipeFilter === 'sort-by'}
          onChange={handleSortByChange}
          onToggle={() => handleToggle('sort-by')}
          recipesSortedBy={sortBy}
        />
      </Flex>
    </Flex>
  )
}
